import React, { useMemo } from 'react'

import { selectIsUnifiedSearchEnabled } from '@disco/state-management/reducers/search-page'
import { hasFilterValue } from '@disco/utils/search-page'

import {
  FilterActionAddButton,
  FilterActionClearAllButton,
  SelectedFilterBadges,
} from 'components/search/search-filters'

import { useSelector } from 'hooks/use-redux'

import type { FilterState } from 'types/filter-page'

import { useFiltersDefinition } from '../utils'

const isFilterValueActive = (value: unknown): boolean => {
  if (value === undefined || value === null) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  return true
}

type Props = {
  filters: FilterState[]
  /** A search term is itself clearable state. */
  hasQuery: boolean
  onChange: (_filters: FilterState[]) => void
  onClearAll: (_filters: FilterState[]) => void
  /**
   * Seed the fixed filters as empty badges on mount for legacy search. Off in
   * Browse, where that mount-time onChange would fire a search.
   */
  seedFixedFilters?: boolean
}

/**
 * Add-filter button, applied badges and clear-all. Shared by the search page
 * and Browse, which bind it to their own state and supply the layout wrapper.
 */
const FilterBar = ({
  filters,
  hasQuery,
  onChange,
  onClearAll,
  seedFixedFilters = true,
}: Props): React.ReactNode => {
  const definitions = useFiltersDefinition()
  const unifiedSearchEnabled = useSelector(selectIsUnifiedSearchEnabled)

  const { hasActiveFilterValues, hasLegacyFilterValues, hasOperator } = useMemo(
    () =>
      filters.reduce(
        (acc, f) => ({
          hasActiveFilterValues:
            acc.hasActiveFilterValues || isFilterValueActive(f.value),
          // Looser than isFilterValueActive on purpose: legacy Reset also
          // treats an empty array as clearable.
          hasLegacyFilterValues:
            acc.hasLegacyFilterValues || hasFilterValue(f.value),
          hasOperator: acc.hasOperator || !!f.operator,
        }),
        {
          hasActiveFilterValues: false,
          hasLegacyFilterValues: false,
          hasOperator: false,
        },
      ),
    [filters],
  )

  const seedDefaultState = seedFixedFilters && !unifiedSearchEnabled
  const hasClearableState = hasActiveFilterValues || hasOperator || hasQuery
  const showClearAll = unifiedSearchEnabled ? hasClearableState : true
  const isClearAllDisabled =
    !unifiedSearchEnabled && !hasLegacyFilterValues && !hasQuery

  return (
    <>
      <FilterActionAddButton
        value={filters}
        definitions={definitions}
        onChange={onChange}
      />
      <SelectedFilterBadges
        value={filters}
        definitions={definitions}
        seedDefaultState={seedDefaultState}
        onChange={onChange}
      />
      {showClearAll ? (
        <FilterActionClearAllButton
          definitions={definitions}
          disabled={isClearAllDisabled}
          seedDefaultState={seedDefaultState}
          onChange={onClearAll}
        />
      ) : null}
    </>
  )
}

export default FilterBar
